import { useMemo, useState } from "react";
import { Modal, Form, Input, message } from "antd";
import {
  useCreateUnion,
  useDeleteUnion,
  useUnionList,
  useUpdateUnion,
} from "../api/address";
import CooperativesTable from "../components/CooperativesTable";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { z } from "zod";
import CooperativeForm from "../components/form/CooperativeForm";
import { DataTable } from "../components/ui/data-table";
import { Union } from "../types/union";
import { Button } from "../components/ui/button";
import { Icons } from "../assets/images/icons";
import { AppButton } from "../components/ui/AppButton";
import { FilterTabs } from "../components/ui/FilterTabs";
import { useInfiniteUnions } from "../hooks/useInfiniteUnions";
import { InfiniteSelect } from "../components/ui/infinite-select";

const unionSchema = z.object({
  unionName: z
    .string()
    .min(1, "Union name is required")
    .regex(/^[A-Za-z\s]+$/, "Union name must contain only letters"),
});

const UnionPage = () => {
  const [form] = Form.useForm();
  const [cooperativeForm] = Form.useForm();

  /** State */
  const [activeFilter, setActiveFilter] = useState<string>("unions");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUnion, setEditingUnion] = useState<any>(null);
  const [selectedUnionId, setSelectedUnionId] = useState<number | undefined>();
  const [isCooperativeModalOpen, setIsCooperativeModalOpen] = useState(false);
  const [isCreatingCooperative] = useState(false);

  const [unionFormErrors, setUnionFormErrors] = useState<{
    unionName?: string;
  }>({});
  const [, setCooperativeFormErrors] = useState<{
    cooperativeName?: string;
  }>({});

  /** API */
  /** Unions */
  const { data: unionData, isLoading: isUnionLoading } = useUnionList({
    page: currentPage - 1,
    size: pageSize,
    enabled: activeFilter === "unions",
  });
  const { isLoading, hasMore, loadMore } = useInfiniteUnions();
  const { mutate: createUnion, isPending: isCreatingUnion } = useCreateUnion();
  const { mutate: updateUnion, isPending: isUpdatingUnion } = useUpdateUnion();
  const { mutate: deleteUnion } = useDeleteUnion();

  /** Transform unions for table */
  const transformedUnions = useMemo((): Union[] => {
    if (!unionData?.data?.content) return [];
    return unionData?.data?.content.map((item: Union) => ({
      id: item.id,
      unionName: item.unionName,
    }));
  }, [unionData]);

  const handleUnionSubmit = async () => {
    try {
      const value = form.getFieldsValue();
      const result = unionSchema.safeParse(value);
      if (!result.success) {
        const fieldErrors: { unionName?: string } = {};
        result.error.issues.forEach((issue) => {
          if (issue.path[0] === "unionName") {
            fieldErrors.unionName = "Union name is required";
          }
          if (issue.message.includes("letters")) {
            fieldErrors.unionName = "Union name must contain only letters";
          }
        });
        setUnionFormErrors(fieldErrors);
        return;
      }

      setUnionFormErrors({});
      const union = { unionName: value.unionName };

      if (isEditMode && editingUnion?.id) {
        updateUnion(
          { id: editingUnion.id, union },
          {
            onSuccess: () => {
              message.success("Union updated");
              setIsModalOpen(false);
              form.resetFields();
              setIsEditMode(false);
            },
            onError: () => message.error("Update failed"),
          }
        );
      } else {
        createUnion(union, {
          onSuccess: () => {
            message.success("Union created");
            setIsModalOpen(false);
            form.resetFields();
          },
          onError: () => message.error("Create failed"),
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (record: any) => {
    setEditingUnion(record);
    setIsEditMode(true);
    form.setFieldsValue({ unionName: record.unionName });
    setIsModalOpen(true);
    setUnionFormErrors({});
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: "Are you sure you want to delete this union?",
      onOk: () => {
        deleteUnion(id.toString(), {
          onSuccess: () => message.success("Union deleted"),
          onError: () => message.error("Delete failed"),
        });
      },
    });
  };

  const handleAddCooperative = (unionId: number) => {
    setSelectedUnionId(unionId);
    setIsCooperativeModalOpen(true);
    cooperativeForm.resetFields();
    setCooperativeFormErrors({});
  };

  const filterTabs = [
    { value: "unions", label: "Unions" },
    { value: "cooperatives", label: "Cooperatives" },
  ];

  /** Handlers */
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const handleScrollEnd = () => {
    loadMore();
  };

  /** Columns */
  const unionColumns = [
    {
      key: "unionName",
      title: "Union Name",
      dataIndex: "unionName",
      align: "left" as const,
    },
    {
      key: "actions",
      title: "Actions",
      align: "right" as const,
      render: (_: any, record: any) => (
        <div className="flex justify-end">
          <Button
            onClick={() => handleEdit(record)}
            variant="secondary"
            className="text-blue-700 hover:text-blue-400 gap-1"
          >
            <EditOutlined className="text-blue-700" />
            Edit
          </Button>
          <Button
            onClick={() => handleDelete(record.id)}
            variant="destructive"
            className="text-red-700 hover:text-red-400 gap-1"
          >
            <DeleteOutlined className="text-red-700" />
            Delete
          </Button>
          <Button
            onClick={() => handleAddCooperative(record.id)}
            variant="ghost"
            className="text-gray-600 hover:text-gray-500"
          >
            Add Cooperative
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-4 bg-white rounded-lg mt-4">
      {/** Header Filters */}
      <div className="space-y-4 mb-6">
        <FilterTabs
          tabs={filterTabs}
          activeTab={activeFilter}
          onTabChange={handleFilterChange}
          className="w-full lg:w-auto"
        />
      </div>

      {/** Desktop Table */}
      <div className="hidden md:block">
        {activeFilter === "unions" ? (
          <>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Manage Unions</h2>

              <AppButton
                customIcon={Icons.addCircle}
                variant="success"
                onClick={() => {
                  setIsEditMode(false);
                  form.resetFields();
                  setIsModalOpen(true);
                  setUnionFormErrors({});
                }}
              >
                Add Union
              </AppButton>
            </div>

            <DataTable<Union>
              data={transformedUnions}
              columns={unionColumns}
              loading={isUnionLoading}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: unionData?.data?.totalElements ?? 0,
                onChange: handlePaginationChange,
              }}
              emptyIcon={<img src={Icons.noDataIcon} alt="No data" />}
              emptyText="No orders found"
              rowKey="id"
            />
          </>
        ) : (
          <>
            <h2 className="text-lg font-semibold mb-2">Manage Cooperatives</h2>

            <InfiniteSelect
              value={selectedUnionId?.toString()}
              onChange={(value) => setSelectedUnionId(Number(value))}
              options={
                unionData?.data?.content
                  ?.filter((u: any) => u.id != null) // ← Filter out null/undefined
                  ?.map((u: any) => ({
                    label: u.unionName || `Union ${u.id}`, // Fallback label
                    value: String(u.id),
                  })) || [] // ← Provide empty array fallback
              }
              onScrollEnd={handleScrollEnd}
              isLoading={isLoading}
              hasMore={hasMore}
              placeholder="Select union"
              className="w-full"
            />

            <CooperativesTable unionId={selectedUnionId} />
          </>
        )}
      </div>

      <Modal
        title={isEditMode ? "Edit Union" : "Add Union"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleUnionSubmit}
        confirmLoading={isCreatingUnion || isUpdatingUnion}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Union Name"
            name="unionName"
            validateStatus={unionFormErrors.unionName ? "error" : ""}
            help={
              unionFormErrors.unionName ? (
                <span style={{ color: "red" }}>
                  {unionFormErrors.unionName}
                </span>
              ) : null
            }
          >
            <Input
              style={{
                borderColor: unionFormErrors.unionName ? "red" : undefined,
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <CooperativeForm
        unionId={selectedUnionId}
        title={""}
        open={isCooperativeModalOpen}
        onCancel={() => setIsCooperativeModalOpen(false)}
        confirmLoading={isCreatingCooperative}
      />
    </div>
  );
};

export default UnionPage;
